import {
  CreateFormTemplateFieldRequest,
  FormTemplateFieldConditionalRule,
  generateFormSlug,
  NonEmptyArray,
} from 'mapistry-shared';
import { v4 as uuidv4 } from 'uuid';
import {
  CreateFormTemplateFieldRequestValues,
  EditableFormTemplateField,
  isTriggerField,
} from '../../types';

export const getFieldCreateRequest = (
  fieldValues: CreateFormTemplateFieldRequestValues,
): CreateFormTemplateFieldRequest => ({
  ...fieldValues,
  id: uuidv4(),
  slug: generateFormSlug(),
});

type FieldIdToCopiedFieldMap = Map<string, EditableFormTemplateField>;

const mapOriginalFieldIdsToCopiedFields = (
  originalFields: EditableFormTemplateField[],
  copiedFields: CreateFormTemplateFieldRequest[],
  errorText: string,
): FieldIdToCopiedFieldMap =>
  new Map<string, EditableFormTemplateField>(
    originalFields.map((originalField, idx) => {
      const copiedField = copiedFields[idx];
      if (!copiedField) throw new Error(errorText);
      return [originalField.id, copiedField];
    }),
  );

export const getFieldCopies = ({
  fields,
  fieldValuesMapper,
  errorMessage,
}: {
  fields: EditableFormTemplateField[];
  fieldValuesMapper: (
    field: EditableFormTemplateField,
  ) => EditableFormTemplateField;
  errorMessage: string;
}): {
  fieldCopies: CreateFormTemplateFieldRequest[];
  originalIdsToNewFields: FieldIdToCopiedFieldMap;
} => {
  const fieldCopiesIgnoringConditions = fields.map((field) =>
    getFieldCreateRequest(fieldValuesMapper(field)),
  );

  const originalIdsToNewFieldsIgnoringConditions =
    mapOriginalFieldIdsToCopiedFields(
      fields,
      fieldCopiesIgnoringConditions,
      errorMessage,
    );

  const fieldCopies = fieldCopiesIgnoringConditions.map((copiedField) => {
    if (!isTriggerField(copiedField)) {
      return copiedField;
    }

    const conditionalRules = copiedField.conditionalRules.map((rule) => {
      const conditionallyShownFields = rule.conditionallyShownFields.map(
        ({ id: originalConditionalFieldId, ...rest }) => {
          const newConditionalField =
            originalIdsToNewFieldsIgnoringConditions.get(
              originalConditionalFieldId,
            );
          if (!newConditionalField) throw new Error(errorMessage);
          return {
            ...rest,
            id: newConditionalField.id,
          };
        },
      );
      return { ...rule, conditionallyShownFields };
    }) as NonEmptyArray<FormTemplateFieldConditionalRule>;

    return {
      ...copiedField,
      conditionalRules,
    };
  });

  const originalIdsToNewFields = mapOriginalFieldIdsToCopiedFields(
    fields,
    fieldCopies,
    errorMessage,
  );

  return { fieldCopies, originalIdsToNewFields };
};
